import React, { useEffect, useMemo, useState } from "react";
import { Select, Space, Spin, Typography, Tag } from "antd";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../variables/constants";
import { estBureauDirect, peutProduirePourCourtier, rightsService } from "../../../../services/rightsService";

const { Text } = Typography;

// Cache module (par code compagnie) pour éviter de recharger les agences, courtiers et
// intermédiaires à chaque ouverture de modal d'avenant → affichage instantané.
const agencesCache = new Map();
const courtiersCache = new Map();
const intermediairesCache = new Map();

/**
 * Sélecteur « Agence / Intermédiaire ciblés pour cet avenant ». La sélection d'AGENCE est réservée
 * au SIÈGE et au BUREAU DIRECT (qui n'y voit que sa propre agence et les courtiers, sauf HLA068) ;
 * la sélection d'INTERMÉDIAIRE est disponible pour TOUS les utilisateurs.
 * Récupère automatiquement le taux de commission de l'agence puis,
 * si un intermédiaire est précisé, le taux de l'intermédiaire (qui prévaut), et
 * remonte le tout au parent via onChange(agence, tauxEffectif, intermediaire).
 *
 * Props :
 *  - organismeCode : code compagnie pour charger les agences/intermédiaires + taux
 *  - defaultAgence : agence initiale (objet)
 *  - categorie     : codeCategorie du contrat (ex. "M") pour récupérer le taux
 *  - onChange      : (agence, tauxEffectif, intermediaire) => void  (optionnel)
 */
const AvenantAgencySelector = ({ organismeCode, defaultAgence, defaultIntermediaire, categorie, onChange }) => {
    const [agences, setAgences] = useState([]);
    const [courtiers, setCourtiers] = useState([]);
    const [loadingCourtiers, setLoadingCourtiers] = useState(false);
    const [selectedAgence, setSelectedAgence] = useState(defaultAgence || null);
    const [taux, setTaux] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingTaux, setLoadingTaux] = useState(false);

    // Intermédiaire (optionnel) — pré-sélectionné sur l'apporteur courant du contrat
    const [intermediaires, setIntermediaires] = useState([]);
    const [selectedIntermediaire, setSelectedIntermediaire] = useState(defaultIntermediaire || null);
    const [interTaux, setInterTaux] = useState(null);
    const [loadingInter, setLoadingInter] = useState(false);
    const [loadingInterTaux, setLoadingInterTaux] = useState(false);

    const peutCiblerAgence = peutProduirePourCourtier();
    // Bureau direct sans HLA068 : « Nos agences » se limite à sa propre agence (même règle qu'à
    // la création) et à l'agence actuelle du contrat, pour pouvoir la garder ; il peut en revanche
    // cibler tous les courtiers agréés.
    const limiteASonAgence = estBureauDirect() && !rightsService("HLA068");
    const agencesVisibles = (list) => limiteASonAgence
        ? (list || []).filter((ag) => ag?.codeAgence === LOGGED_USER?.agence?.codeAgence
            || ag?.codeAgence === defaultAgence?.codeAgence)
        : (list || []);

    const codeOrganisme = organismeCode || LOGGED_USER?.profil?.organisme?.code;

    // Comparateur alphabétique croissant (français, insensible casse/accents).
    const sortByLabelAsc = (a, b) =>
        String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" });

    // On n'affiche QUE les agences ouvertes à la production (restrictionProduction ≠ true).
    // C'est ce filtre — et non une dédup — qui lève la « répétition SAVANA » : deux agences
    // DIFFÉRENTES peuvent partager un même code (ex. SAVANA LOME active vs SAVANA KARA
    // restreinte, toutes deux code 2027) ; masquer la restreinte laisse un seul code → plus de
    // collision de `value` dans la liste virtualisée. `!== true` conserve les `false` ET les
    // `null` (agences legacy sans le champ), pour ne pas les faire disparaître.
    const productionActive = (ag) => ag?.restrictionProduction !== true;
    // Dédup par `value` : ultime garde-fou contre la répétition au scroll si deux options
    // gardaient malgré tout le même code.
    const dedupeByValue = (options) => {
        const seen = new Set();
        return options.filter((o) => o.value != null && !seen.has(o.value) && seen.add(o.value));
    };
    const toOption = (ag) => ({
        label: `${ag.nom} (${ag.codeAgence || ag.codeId || "?"})`,
        value: ag.codeAgence || ag.codeId,
    });

    // Options de l'AGENCE CIBLÉE, en DEUX sous-groupes : nos agences et nos courtiers agréés.
    // Chaque groupe est filtré (production active), dédupliqué puis trié. Un groupe vide est masqué.
    const agenceOptions = useMemo(() => {
        const nosAgences = dedupeByValue(agencesVisibles(agences).filter(productionActive).map(toOption)).sort(sortByLabelAsc);
        const nosCourtiers = dedupeByValue((courtiers || []).filter(productionActive).map(toOption)).sort(sortByLabelAsc);
        const groups = [];
        if (nosAgences.length) groups.push({ label: "Nos agences", options: nosAgences });
        if (nosCourtiers.length) groups.push({ label: "Nos courtiers", options: nosCourtiers });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agences, courtiers]);

    // Options des intermédiaires, triées par nom (repli sur le code si vide).
    // On garantit la présence de l'intermédiaire pré-sélectionné même si la liste
    // n'est pas encore chargée, pour un affichage immédiat de son libellé.
    const intermediaireOptions = useMemo(() => {
        const source = [...(intermediaires || [])];
        if (selectedIntermediaire?.codeIntermediaire
            && !source.some((i) => i.codeIntermediaire === selectedIntermediaire.codeIntermediaire)) {
            source.push(selectedIntermediaire);
        }
        return source
            .map((inter) => {
                const fullName = `${inter?.personne?.nom ?? ""} ${inter?.personne?.prenom ?? ""}`.trim();
                return {
                    label: fullName || inter?.codeIntermediaire || "",
                    value: inter.codeIntermediaire,
                };
            })
            .sort(sortByLabelAsc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intermediaires, selectedIntermediaire]);

    // Taux effectif : l'intermédiaire prévaut sur l'agence quand il est précisé.
    const computeEffectiveTaux = (agenceTaux, intermediaire, intermediaireTaux) =>
        intermediaire && intermediaireTaux != null ? intermediaireTaux : agenceTaux;

    const emit = (agence, agenceTaux, intermediaire, intermediaireTaux) => {
        if (onChange) {
            onChange(agence, computeEffectiveTaux(agenceTaux, intermediaire, intermediaireTaux), intermediaire || null);
        }
    };

    // Récupère le taux de commission d'une agence pour la catégorie du contrat
    const fetchTaux = async (codeAgence) => {
        const codeCategorie = (categorie || "").trim();
        if (!codeAgence || !codeOrganisme || !codeCategorie) {
            setTaux(null);
            return null;
        }
        setLoadingTaux(true);
        try {
            const res = await axiosInstance.get(
                `/api/agence/taux-commission/${codeAgence}/${codeCategorie}/${codeOrganisme}`);
            const t = Number(res?.data);
            const value = !Number.isNaN(t) ? t : null;
            setTaux(value);
            return value;
        } catch (err) {
            console.log(err);
            setTaux(null);
            return null;
        } finally {
            setLoadingTaux(false);
        }
    };

    // Récupère le taux de commission d'un intermédiaire pour la catégorie
    const fetchInterTaux = async (codeIntermediaire) => {
        const codeCategorie = (categorie || "").trim();
        if (!codeIntermediaire || !codeOrganisme || !codeCategorie) {
            setInterTaux(null);
            return null;
        }
        setLoadingInterTaux(true);
        try {
            const res = await axiosInstance.get(
                `/api/intermediaire/taux-commission/${codeIntermediaire}/${codeCategorie}/${codeOrganisme}`);
            const t = Number(res?.data);
            const value = !Number.isNaN(t) ? t : null;
            setInterTaux(value);
            return value;
        } catch (err) {
            console.log(err);
            setInterTaux(null);
            return null;
        } finally {
            setLoadingInterTaux(false);
        }
    };

    // Chargement des agences (siège / bureau direct) — avec cache module
    useEffect(() => {
        if (!peutCiblerAgence) return;
        const cacheKey = codeOrganisme || "__assurances__";
        if (agencesCache.has(cacheKey)) {
            const cached = agencesCache.get(cacheKey);
            setAgences(cached);
            if (!selectedAgence && agencesVisibles(cached).length) setSelectedAgence(agencesVisibles(cached)[0]);
            return;
        }
        const endpoint = codeOrganisme
            ? `/api/agence/organisme/code/${codeOrganisme}`
            : `/api/agence/assurances`;
        setLoading(true);
        axiosInstance
            .get(endpoint)
            .then((res) => {
                const agencies = Array.isArray(res.data) ? res.data : [];
                agencesCache.set(cacheKey, agencies);
                setAgences(agencies);
                if (!selectedAgence && agencesVisibles(agencies).length) {
                    setSelectedAgence(agencesVisibles(agencies)[0]);
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeOrganisme, peutCiblerAgence]);

    // Chargement des COURTIERS AGRÉÉS de la compagnie (siège / bureau direct, comme les agences) —
    // avec cache module. Endpoint existant : renvoie les courtiers ayant un agrément avec la
    // compagnie. Ils forment le sous-groupe « Nos courtiers » du sélecteur d'agence ciblée.
    useEffect(() => {
        if (!peutCiblerAgence || !codeOrganisme) return;
        if (courtiersCache.has(codeOrganisme)) {
            setCourtiers(courtiersCache.get(codeOrganisme));
            return;
        }
        setLoadingCourtiers(true);
        axiosInstance
            .get(`/api/agence/organisme/courtiers/${codeOrganisme}`)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                courtiersCache.set(codeOrganisme, list);
                setCourtiers(list);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoadingCourtiers(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeOrganisme, peutCiblerAgence]);

    // Chargement des intermédiaires actifs de la compagnie (TOUS les utilisateurs) — avec cache module.
    useEffect(() => {
        if (!codeOrganisme) return;
        if (intermediairesCache.has(codeOrganisme)) {
            setIntermediaires(intermediairesCache.get(codeOrganisme));
            return;
        }
        setLoadingInter(true);
        axiosInstance
            .get(`/api/intermediaire/compagnie/actifs/${codeOrganisme}`)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                intermediairesCache.set(codeOrganisme, list);
                setIntermediaires(list);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoadingInter(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeOrganisme, peutCiblerAgence]);

    // Récupère le taux initial dès qu'on connaît l'agence + la catégorie, et émet
    // d'emblée l'intermédiaire courant (pré-sélectionné) pour qu'il soit toujours
    // ré-appliqué/persisté — garantit son affichage sur la condition particulière.
    useEffect(() => {
        if (!categorie) return;
        // Taux agence : seulement si une agence est connue (siège : sélectionnée ; non-siège : agence
        // du contrat). Taux intermédiaire : si un intermédiaire est (pré)sélectionné. L'intermédiaire prévaut.
        Promise.all([
            selectedAgence?.codeAgence ? fetchTaux(selectedAgence.codeAgence) : Promise.resolve(null),
            selectedIntermediaire?.codeIntermediaire
                ? fetchInterTaux(selectedIntermediaire.codeIntermediaire)
                : Promise.resolve(null),
        ]).then(([agTaux, itTaux]) => {
            emit(selectedAgence, agTaux, selectedIntermediaire, itTaux);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgence?.codeAgence, categorie]);

    const handleChange = async (codeAgence) => {
        // Rechercher dans les DEUX sources (agences + courtiers agréés) : le `value` de l'option
        // est codeAgence (ou codeId à défaut), et un courtier ciblé est une agence à part entière.
        // On restreint aux agences ouvertes à la production : deux agences peuvent partager un
        // même code (SAVANA LOME vs KARA), il faut retenir CELLE affichée (non restreinte).
        const ag = [...agences, ...courtiers]
            .filter((a) => a?.restrictionProduction !== true)
            .find((a) => (a.codeAgence || a.codeId) === codeAgence) || null;
        setSelectedAgence(ag);
        const t = await fetchTaux(codeAgence);
        emit(ag, t, selectedIntermediaire, interTaux);
    };

    const handleIntermediaireChange = async (codeIntermediaire) => {
        const inter = intermediaires.find((i) => i.codeIntermediaire === codeIntermediaire) || null;
        setSelectedIntermediaire(inter);
        const it = inter ? await fetchInterTaux(codeIntermediaire) : null;
        if (!inter) setInterTaux(null);
        emit(selectedAgence, taux, inter, it);
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {/* Sélection de l'AGENCE ciblée : siège et bureau direct (pas d'habilitation). */}
                {peutCiblerAgence && (
                    <>
                        <Text strong>Agence ciblée pour cet avenant</Text>
                        <Select
                            showSearch
                            style={{ width: "100%" }}
                            placeholder="Sélectionnez l'agence"
                            value={selectedAgence?.codeAgence || selectedAgence?.codeId || undefined}
                            onChange={handleChange}
                            loading={loading || loadingCourtiers}
                            optionFilterProp="label"
                            filterSort={sortByLabelAsc}
                            notFoundContent={(loading || loadingCourtiers) ? <Spin size="small" /> : null}
                            options={agenceOptions}
                        />
                        {categorie && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Taux de commission :{" "}
                                {loadingTaux ? (
                                    <Spin size="small" />
                                ) : (
                                    <Tag color={taux > 0 ? "green" : "default"}>
                                        {taux != null ? `${taux} %` : "non défini"}
                                    </Tag>
                                )}
                                <span> · auto-récupéré de l'agence sélectionnée</span>
                            </Text>
                        )}
                    </>
                )}

                {/* Sélection de l'INTERMÉDIAIRE : disponible pour TOUS les utilisateurs. */}
                <Text strong style={{ marginTop: peutCiblerAgence ? 8 : 0 }}>Intermédiaire ciblé pour cet avenant</Text>
                <Select
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Sélectionnez l'intermédiaire (optionnel)"
                    value={selectedIntermediaire?.codeIntermediaire || undefined}
                    onChange={handleIntermediaireChange}
                    loading={loadingInter}
                    optionFilterProp="label"
                    filterSort={sortByLabelAsc}
                    notFoundContent={loadingInter ? <Spin size="small" /> : null}
                    options={intermediaireOptions}
                />
                {categorie && selectedIntermediaire && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Taux de commission :{" "}
                        {loadingInterTaux ? (
                            <Spin size="small" />
                        ) : (
                            <Tag color={interTaux > 0 ? "green" : "default"}>
                                {interTaux != null ? `${interTaux} %` : "non défini"}
                            </Tag>
                        )}
                        <span> · l'intermédiaire prévaut sur l'agence</span>
                    </Text>
                )}
            </Space>
        </div>
    );
};

export default AvenantAgencySelector;
